import YesNoToggle from '@orn/shared-web/components/YesNoToggle';

import { OnboardingTitle } from '../../../OnboardingLayout';
import { getDependentNames, getDisplayName } from '../dependentUtils';
import OnboardingStepActions from '../OnboardingStepActions';

import { isPartnershipLivingApart, type IResidenceDraft, type IResidenceFormState } from './residenceDraft';

import type { IDependent } from '../../../../hooks/queries/useDependents';

interface IResidenceArrangementPageProps {
  dependents: ReadonlyArray<IDependent>;
  isPartnership: boolean;
  draft: IResidenceDraft;
  onLiveTogetherChange: (value: boolean) => void;
  onParentAFormChange: (next: IResidenceFormState) => void;
  onParentBFormChange: (next: IResidenceFormState) => void;
  isContinueDisabled: boolean;
  /** Advances to the details page — persists nothing. */
  onContinue: () => void;
  /** Inline back-arrow handler — the wizard's previous screen. */
  onBack?: () => void;
  /** Skips the entire residence section (both pages). */
  onSkip: () => void;
}

/**
 * Page 1 of the residence section — "Living situation". Captures the
 * living-arrangement toggles only: "do they live together?" for a partnership,
 * then "do they live with you?" (one shared answer, or one per parent when the
 * partnership lives apart). Writes only to the in-progress draft; nothing
 * persists here. Continue advances to the details page; Skip bypasses the whole
 * section. Purely presentational — all state and navigation come from props.
 */
const ResidenceArrangementPage = ({
  dependents,
  isPartnership,
  draft,
  onLiveTogetherChange,
  onParentAFormChange,
  onParentBFormChange,
  isContinueDisabled,
  onContinue,
  onBack,
  onSkip,
}: IResidenceArrangementPageProps) => {
  const dependentA = dependents[0];
  const dependentB = dependents[1] ?? null;
  const allNames = getDependentNames(dependents);
  const aName = getDisplayName(dependentA);
  const bName = dependentB !== null ? getDisplayName(dependentB) : '';

  const apart = isPartnershipLivingApart(draft, isPartnership);
  const liveTogetherLabel = `Do ${allNames} live together?`;
  // Single dependent names them; a partnership uses "they" to avoid repeating
  // both names on every line.
  const sharedLiveWithCaregiverLabel =
    dependents.length === 1 ? `Does ${aName} live with you?` : 'Do they live with you?';

  return (
    <>
      <OnboardingTitle title="Living situation" onBack={onBack} />

      <div className="flex w-full flex-col gap-6">
        {isPartnership && (
          <YesNoToggle label={liveTogetherLabel} value={draft.liveTogether} onChange={onLiveTogetherChange} />
        )}

        {apart ? (
          <>
            <YesNoToggle
              label={`Does ${aName} live with you?`}
              value={draft.parentAForm.liveWithCaregiver}
              onChange={(v) => onParentAFormChange({ ...draft.parentAForm, liveWithCaregiver: v })}
            />
            {dependentB !== null && (
              <YesNoToggle
                label={`Does ${bName} live with you?`}
                value={draft.parentBForm.liveWithCaregiver}
                onChange={(v) => onParentBFormChange({ ...draft.parentBForm, liveWithCaregiver: v })}
              />
            )}
          </>
        ) : (
          <YesNoToggle
            label={sharedLiveWithCaregiverLabel}
            value={draft.parentAForm.liveWithCaregiver}
            onChange={(v) => onParentAFormChange({ ...draft.parentAForm, liveWithCaregiver: v })}
          />
        )}
      </div>

      <OnboardingStepActions isContinueDisabled={isContinueDisabled} onContinue={onContinue} onSkip={onSkip} />
    </>
  );
};

ResidenceArrangementPage.displayName = 'ResidenceArrangementPage';

export default ResidenceArrangementPage;
